/**
 * State Template Library Manager Component
 * 
 * Manages reusable state machine templates that can be applied to artifact types.
 * Templates define common workflows like "Operational Lifecycle" and "Improvement Lifecycle".
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Tooltip,
  Popconfirm,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  SettingOutlined,
  ReloadOutlined,
  FileTextOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import { StateTemplateStatesTransitionsEditor } from './StateTemplateStatesTransitionsEditor';
import './StateTemplateLibraryManager.css';

const { Option } = Select;
const { TextArea } = Input;

// Helper functions to deduplicate states and transitions by name
const deduplicateStates = (statesArray: StateTemplateState[]): StateTemplateState[] => {
  const seen = new Set<string>();
  return statesArray.filter(state => {
    if (seen.has(state.name)) {
      return false;
    }
    seen.add(state.name);
    return true;
  });
};

const deduplicateTransitions = (transitionsArray: StateTemplateTransition[]): StateTemplateTransition[] => {
  const seen = new Set<string>();
  return transitionsArray.filter(transition => {
    if (seen.has(transition.name)) {
      return false;
    }
    seen.add(transition.name);
    return true;
  });
};

interface StateTemplateState {
  id: string;
  template_id: string;
  name: string;
  description?: string;
  is_initial: boolean;
  is_final: boolean;
  is_improvement: boolean;
  is_resource_active: boolean;
  state_type: string;
  sequence_order: number;
  default_parameters?: Record<string, any>;
}

interface StateTemplateTransition {
  id: string;
  template_id: string;
  name: string;
  description?: string;
  from_state: string | null;
  to_state: string;
  default_transition_type: string;
  is_secondary: boolean;
  is_improvement: boolean;
  sequence_order: number;
  default_parameters?: Record<string, any>;
  validation_rules?: Record<string, any>;
}

interface StateTemplatePropertyMapping {
  id: string;
  template_id: string;
  property_key: string;
  property_type: string;
  global_option_catalog_ref?: string;
  created_in_states?: string[];
  updated_in_states?: string[];
  referenced_artifact_type_ids?: string[];
  is_improvement?: boolean;
}

interface StateTemplate {
  id: string;
  name: string;
  description?: string;
  version: string;
  applies_to_artifact_types?: string[];
  tags?: string[];
  created_at: string;
  updated_at: string;
  states: StateTemplateState[];
  transitions: StateTemplateTransition[];
  property_mappings: StateTemplatePropertyMapping[];
}

export const StateTemplateLibraryManager: React.FC = () => {
  const [templates, setTemplates] = useState<StateTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<StateTemplate | null>(null);
  const [form] = Form.useForm();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<StateTemplate | null>(null);
  const [showStatesTransitionsModal, setShowStatesTransitionsModal] = useState(false);
  const [editingTemplateForStates, setEditingTemplateForStates] = useState<StateTemplate | null>(null);
  const [copyingFromTemplate, setCopyingFromTemplate] = useState<StateTemplate | null>(null);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/state-templates');
      const templates = response.data || [];
      
      // Check if any templates are missing relationship data (showing 0 counts)
      const templatesNeedingFullData = templates.filter((template: StateTemplate) => {
        const statesCount = Array.isArray(template.states) ? template.states.length : 0;
        const transitionsCount = Array.isArray(template.transitions) ? template.transitions.length : 0;
        const propertiesCount = Array.isArray(template.property_mappings) ? template.property_mappings.length : 0;
        // If all counts are 0, we likely need to fetch the full template data
        return statesCount === 0 && transitionsCount === 0 && propertiesCount === 0;
      });
      
      // If we have templates that need full data, fetch them individually
      let enrichedTemplates = [...templates];
      if (templatesNeedingFullData.length > 0) {
        console.log(`Fetching full data for ${templatesNeedingFullData.length} templates that showed 0 counts`);
        
        // Fetch full data for templates that need it (in parallel)
        const fullDataPromises = templatesNeedingFullData.map(async (template: StateTemplate) => {
          try {
            const fullResponse = await apiClient.get(`/state-templates/${template.id}`);
            return fullResponse.data;
          } catch (error) {
            console.error(`Error fetching full data for template ${template.id}:`, error);
            return template; // Fallback to original template
          }
        });
        
        const fullDataTemplates = await Promise.all(fullDataPromises);
        
        // Replace templates in the list with their full data versions
        enrichedTemplates = templates.map((template: StateTemplate) => {
          const fullData = fullDataTemplates.find((ft: StateTemplate) => ft.id === template.id);
          return fullData || template;
        });
      }
      
      // Log template data for debugging
      enrichedTemplates.forEach((template: StateTemplate) => {
        const statesCount = Array.isArray(template.states) ? template.states.length : 0;
        const transitionsCount = Array.isArray(template.transitions) ? template.transitions.length : 0;
        const propertiesCount = Array.isArray(template.property_mappings) ? template.property_mappings.length : 0;
        console.log(`Template "${template.name}": ${statesCount} states, ${transitionsCount} transitions, ${propertiesCount} properties`);
      });
      
      // Deduplicate states and transitions for each template
      const cleanedTemplates = enrichedTemplates.map((template: StateTemplate) => {
        // Ensure we have arrays
        const states = Array.isArray(template.states) ? template.states : [];
        const transitions = Array.isArray(template.transitions) ? template.transitions : [];
        const propertyMappings = Array.isArray(template.property_mappings) ? template.property_mappings : [];
        
        return {
          ...template,
          states: deduplicateStates(states),
          transitions: deduplicateTransitions(transitions.map((t: StateTemplateTransition) => ({
            ...t,
            is_improvement: t.is_improvement ?? false
          }))),
          property_mappings: propertyMappings.map((p: StateTemplatePropertyMapping) => ({
            ...p,
            is_improvement: p.is_improvement ?? false
          }))
        };
      });
      
      setTemplates(cleanedTemplates);
    } catch (error) {
      console.error('Error fetching templates:', error);
      message.error('Failed to fetch state templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchTemplates();
  }, [fetchTemplates]);

  const handleAddTemplate = (sourceTemplate?: StateTemplate) => {
    setEditingTemplate(null);
    if (sourceTemplate) {
      // Pre-fill form with source template data
      form.setFieldsValue({
        name: `Copy of ${sourceTemplate.name}`,
        description: sourceTemplate.description,
        version: '1.0.0',
        applies_to_artifact_types: sourceTemplate.applies_to_artifact_types,
        tags: sourceTemplate.tags
      });
      setCopyingFromTemplate(sourceTemplate);
    } else {
      form.resetFields();
      setCopyingFromTemplate(null);
    }
    setShowModal(true);
  };

  const handleCopyTemplate = async (template: StateTemplate) => {
    try {
      // Fetch the full template data to ensure we have states, transitions, and property_mappings
      const response = await apiClient.get(`/state-templates/${template.id}`);
      const fetchedTemplate = response.data;
      
      // Ensure states and transitions are deduplicated
      const cleanedTemplate = {
        ...fetchedTemplate,
        states: deduplicateStates(fetchedTemplate.states || []),
        transitions: deduplicateTransitions((fetchedTemplate.transitions || []).map((t: StateTemplateTransition) => ({
          ...t,
          is_improvement: t.is_improvement ?? false
        }))),
        property_mappings: (fetchedTemplate.property_mappings || []).map((p: StateTemplatePropertyMapping) => ({
          ...p,
          is_improvement: p.is_improvement ?? false
        }))
      };
      
      console.log('Copying template:', cleanedTemplate.name, 'with', cleanedTemplate.states?.length || 0, 'states,', cleanedTemplate.transitions?.length || 0, 'transitions, and', cleanedTemplate.property_mappings?.length || 0, 'properties');
      
      handleAddTemplate(cleanedTemplate);
    } catch (error: any) {
      console.error('Error fetching template for copying:', error);
      message.error('Failed to load template for copying: ' + (error.response?.data?.detail || error.message));
      // Fallback to using the template from the list
      handleAddTemplate(template);
    }
  };

  const handleEditTemplate = (template: StateTemplate) => {
    setEditingTemplate(template);
    form.setFieldsValue({
      name: template.name,
      description: template.description,
      version: template.version,
      applies_to_artifact_types: template.applies_to_artifact_types,
      tags: template.tags
    });
    setShowModal(true);
  };

  const handleViewDetails = (template: StateTemplate) => {
    setSelectedTemplate(template);
    setShowDetailsModal(true);
  };

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      await apiClient.delete(`/state-templates/${templateId}`);
      message.success('Template deleted successfully');
      await fetchTemplates();
    } catch (error: any) {
      message.error('Failed to delete template: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditStatesTransitions = async (template: StateTemplate) => {
    try {
      // Fetch the template by ID to ensure we have the latest data with all relationships
      const response = await apiClient.get(`/state-templates/${template.id}`);
      const fetchedTemplate = response.data;
      
      // Ensure states and transitions are deduplicated before editing
      const cleanedTemplate = {
        ...fetchedTemplate,
        states: deduplicateStates(fetchedTemplate.states || []),
        transitions: deduplicateTransitions((fetchedTemplate.transitions || []).map((t: StateTemplateTransition) => ({
          ...t,
          is_improvement: t.is_improvement ?? false
        }))),
        property_mappings: (fetchedTemplate.property_mappings || []).map((p: StateTemplatePropertyMapping) => ({
          ...p,
          is_improvement: p.is_improvement ?? false
        }))
      };
      
      console.log('Fetched template for editing:', cleanedTemplate.name, 'with', cleanedTemplate.states?.length || 0, 'states and', cleanedTemplate.transitions?.length || 0, 'transitions');
      
      setEditingTemplateForStates(cleanedTemplate);
      setShowStatesTransitionsModal(true);
    } catch (error: any) {
      console.error('Error fetching template for editing:', error);
      message.error('Failed to load template: ' + (error.response?.data?.detail || error.message));
      // Fallback to using the template from the list
      const cleanedTemplate = {
        ...template,
        states: deduplicateStates(template.states || []),
        transitions: deduplicateTransitions(template.transitions || [])
      };
      setEditingTemplateForStates(cleanedTemplate);
      setShowStatesTransitionsModal(true);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      const values = await form.validateFields();

      // Clean up empty tags and applies_to_artifact_types
      const cleanedValues = {
        ...values,
        tags: values.tags && values.tags.length > 0 ? values.tags : null,
        applies_to_artifact_types: values.applies_to_artifact_types && values.applies_to_artifact_types.length > 0 ? values.applies_to_artifact_types : null
      };

      if (editingTemplate) {
        // Update existing template
        await apiClient.put(`/state-templates/${editingTemplate.id}`, cleanedValues);
        message.success('Template updated successfully');
      } else {
        // Create new template
        let templateData: any = {
          ...cleanedValues,
          states: [],
          transitions: [],
          property_mappings: []
        };

        // If copying from an existing template, include its states, transitions, and properties
        if (copyingFromTemplate) {
          // Copy states (remove IDs so new ones are created)
          templateData.states = (copyingFromTemplate.states || []).map(state => ({
            name: state.name,
            description: state.description,
            is_initial: state.is_initial,
            is_final: state.is_final,
            is_improvement: state.is_improvement,
            is_resource_active: state.is_resource_active,
            state_type: state.state_type,
            sequence_order: state.sequence_order,
            default_parameters: state.default_parameters
          }));

          // Copy transitions (remove IDs so new ones are created)
          templateData.transitions = (copyingFromTemplate.transitions || []).map(transition => ({
            name: transition.name,
            description: transition.description,
            from_state: transition.from_state,
            to_state: transition.to_state,
            default_transition_type: transition.default_transition_type,
            is_secondary: transition.is_secondary,
            is_improvement: transition.is_improvement || false,
            sequence_order: transition.sequence_order,
            default_parameters: transition.default_parameters,
            validation_rules: transition.validation_rules
          }));

          // Copy property mappings (remove IDs so new ones are created)
          templateData.property_mappings = (copyingFromTemplate.property_mappings || []).map(mapping => ({
            property_key: mapping.property_key,
            property_type: mapping.property_type,
            global_option_catalog_ref: mapping.global_option_catalog_ref,
            created_in_states: mapping.created_in_states,
            updated_in_states: mapping.updated_in_states,
            referenced_artifact_type_ids: mapping.referenced_artifact_type_ids,
            is_improvement: mapping.is_improvement || false
          }));
        }

        const response = await apiClient.post('/state-templates', templateData);
        const createdTemplate = response.data;
        console.log('Template created:', createdTemplate.name, 'with', createdTemplate.states?.length || 0, 'states,', createdTemplate.transitions?.length || 0, 'transitions, and', createdTemplate.property_mappings?.length || 0, 'properties');
        
        // Only show success if we actually got data back
        if (createdTemplate && createdTemplate.id) {
          message.success(`Template created successfully with ${createdTemplate.states?.length || 0} states, ${createdTemplate.transitions?.length || 0} transitions, and ${createdTemplate.property_mappings?.length || 0} properties`);
        } else {
          message.warning('Template created but response data may be incomplete');
        }
      }

      setShowModal(false);
      setCopyingFromTemplate(null);
      await fetchTemplates();
    } catch (error: any) {
      console.error('Error saving template:', error);
      message.error('Failed to save template: ' + (error.response?.data?.detail || error.message));
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text: string, record: StateTemplate) => (
        <Space>
          <strong>{text}</strong>
          {record.tags && Array.isArray(record.tags) && record.tags.length > 0 && (
            <Space size={4}>
              {record.tags.map((tag, idx) => (
                <Tag key={idx} color="blue">{tag}</Tag>
              ))}
            </Space>
          )}
        </Space>
      )
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: 100
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'States',
      key: 'states_count',
      width: 100,
      render: (record: StateTemplate) => (
        <Tag color="cyan">{record.states?.length || 0}</Tag>
      )
    },
    {
      title: 'Transitions',
      key: 'transitions_count',
      width: 120,
      render: (record: StateTemplate) => (
        <Tag color="green">{record.transitions?.length || 0}</Tag>
      )
    },
    {
      title: 'Properties',
      key: 'properties_count',
      width: 120,
      render: (record: StateTemplate) => (
        <Tag color="purple">{record.property_mappings?.length || 0}</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right' as const,
      render: (record: StateTemplate) => (
        <Space>
          <Tooltip title="View details">
            <Button
              type="link"
              icon={<FileTextOutlined />}
              onClick={() => handleViewDetails(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Edit template metadata">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditTemplate(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Edit states and transitions">
            <Button
              type="link"
              icon={<SettingOutlined />}
              onClick={() => handleEditStatesTransitions(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Copy template">
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => handleCopyTemplate(record)}
              size="small"
            />
          </Tooltip>
          <Popconfirm
            title="Delete this template?"
            description="This will remove the template. Are you sure?"
            onConfirm={() => handleDeleteTemplate(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete template">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="state-template-library-manager">
      <Card
        title={
          <Space>
            <SettingOutlined />
            <span>State Template Library</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchTemplates()}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleAddTemplate()}
            >
              Create Template
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={templates}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} templates`
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingTemplate ? 'Edit Template' : copyingFromTemplate ? 'Copy Template' : 'Create Template'}
        open={showModal}
        onOk={handleSaveTemplate}
        onCancel={() => {
          setShowModal(false);
          setCopyingFromTemplate(null);
        }}
        width={600}
        okText="Save"
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ version: '1.0.0' }}
        >
          {!editingTemplate && !copyingFromTemplate && (
            <Form.Item
              label="Copy from Existing Template"
              name="copy_from_template"
              tooltip="Optionally copy states, transitions, and properties from an existing template"
            >
              <Select
                placeholder="Select a template to copy from (optional)"
                allowClear
                showSearch
                optionFilterProp="children"
                onChange={async (templateId) => {
                  if (templateId) {
                    try {
                      // Fetch the full template data to ensure we have states, transitions, and property_mappings
                      const response = await apiClient.get(`/state-templates/${templateId}`);
                      const fetchedTemplate = response.data;
                      
                      // Ensure states and transitions are deduplicated
                      const cleanedTemplate = {
                        ...fetchedTemplate,
                        states: deduplicateStates(fetchedTemplate.states || []),
                        transitions: deduplicateTransitions((fetchedTemplate.transitions || []).map((t: StateTemplateTransition) => ({
                          ...t,
                          is_improvement: t.is_improvement ?? false
                        }))),
                        property_mappings: (fetchedTemplate.property_mappings || []).map((p: StateTemplatePropertyMapping) => ({
                          ...p,
                          is_improvement: p.is_improvement ?? false
                        }))
                      };
                      
                      console.log('Selected template for copying:', cleanedTemplate.name, 'with', cleanedTemplate.states?.length || 0, 'states,', cleanedTemplate.transitions?.length || 0, 'transitions, and', cleanedTemplate.property_mappings?.length || 0, 'properties');
                      handleAddTemplate(cleanedTemplate);
                    } catch (error: any) {
                      console.error('Error fetching template for copying:', error);
                      message.error('Failed to load template: ' + (error.response?.data?.detail || error.message));
                      // Fallback to using the template from the list
                      const sourceTemplate = templates.find(t => t.id === templateId);
                      if (sourceTemplate) {
                        handleAddTemplate(sourceTemplate);
                      }
                    }
                  } else {
                    setCopyingFromTemplate(null);
                    form.setFieldsValue({
                      name: '',
                      description: '',
                      version: '1.0.0',
                      applies_to_artifact_types: null,
                      tags: null
                    });
                  }
                }}
              >
                {templates.map(template => (
                  <Option key={template.id} value={template.id}>
                    {template.name} (v{template.version})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {copyingFromTemplate && (
            <div style={{ marginBottom: 16, padding: 12, background: '#e6f7ff', borderRadius: 4 }}>
              <p style={{ margin: 0, color: '#1890ff' }}>
                <strong>Copying from:</strong> {copyingFromTemplate.name} (v{copyingFromTemplate.version})
              </p>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                This will copy {copyingFromTemplate.states?.length || 0} states, {copyingFromTemplate.transitions?.length || 0} transitions, 
                and {copyingFromTemplate.property_mappings?.length || 0} properties
              </p>
            </div>
          )}

          <Form.Item
            label="Template Name"
            name="name"
            rules={[{ required: true, message: 'Please enter template name' }]}
          >
            <Input placeholder="e.g., Operational Lifecycle" />
          </Form.Item>

          <Form.Item
            label="Version"
            name="version"
            rules={[{ required: true, message: 'Please enter version' }]}
          >
            <Input placeholder="e.g., 1.0.0" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
          >
            <TextArea rows={3} placeholder="Describe this template" />
          </Form.Item>

          <Form.Item
            label="Tags"
            name="tags"
            getValueFromEvent={(value) => value || []}
            normalize={(value) => Array.isArray(value) ? value : []}
          >
            <Select
              mode="tags"
              placeholder="Add tags (optional)"
              tokenSeparators={[',']}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Details Modal */}
      <Modal
        title="Template Details"
        open={showDetailsModal}
        onCancel={() => setShowDetailsModal(false)}
        footer={null}
        width={900}
      >
        {selectedTemplate && (
          <div>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <h3>Template Information</h3>
                <p><strong>Name:</strong> {selectedTemplate.name}</p>
                <p><strong>Version:</strong> {selectedTemplate.version}</p>
                <p><strong>Description:</strong> {selectedTemplate.description || 'N/A'}</p>
              </div>

              <Divider />

              <div>
                {(() => {
                  const uniqueStates = deduplicateStates(selectedTemplate.states || []);
                  return (
                    <>
                      <h3>States ({uniqueStates.length})</h3>
                      <Table
                        dataSource={uniqueStates}
                        rowKey={(record) => record.id || record.name || `state-${record.name}`}
                        pagination={false}
                        size="small"
                        columns={[
                          { title: 'Name', dataIndex: 'name', key: 'name' },
                          { title: 'Type', dataIndex: 'state_type', key: 'state_type' },
                          {
                            title: 'Flags',
                            key: 'flags',
                            render: (_: any, record: StateTemplateState) => (
                              <Space>
                                {record.is_initial && <Tag color="green">Initial</Tag>}
                                {record.is_final && <Tag color="red">Final</Tag>}
                                {record.is_improvement && <Tag color="purple">Improvement</Tag>}
                              </Space>
                            )
                          }
                        ]}
                      />
                    </>
                  );
                })()}
              </div>

              <Divider />

              <div>
                {(() => {
                  const uniqueTransitions = deduplicateTransitions(selectedTemplate.transitions || []);
                  return (
                    <>
                      <h3>Transitions ({uniqueTransitions.length})</h3>
                      <Table
                        dataSource={uniqueTransitions}
                        rowKey={(record) => record.id || record.name || `state-${record.name}`}
                        pagination={false}
                        size="small"
                        columns={[
                          { title: 'Name', dataIndex: 'name', key: 'name' },
                          { title: 'From', dataIndex: 'from_state', key: 'from_state', render: (text: string | null) => text || 'N/A' },
                          { title: 'To', dataIndex: 'to_state', key: 'to_state' },
                          { title: 'Type', dataIndex: 'default_transition_type', key: 'default_transition_type' }
                        ]}
                      />
                    </>
                  );
                })()}
              </div>

              <Divider />

              <div>
                <h3>Property Mappings ({selectedTemplate.property_mappings?.length || 0})</h3>
                {selectedTemplate.property_mappings && selectedTemplate.property_mappings.length > 0 ? (
                  <Table
                    dataSource={selectedTemplate.property_mappings}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    columns={[
                      { 
                        title: 'Property Key', 
                        dataIndex: 'property_key', 
                        key: 'property_key',
                        render: (text: string) => <strong>{text}</strong>
                      },
                      { 
                        title: 'Type', 
                        dataIndex: 'property_type', 
                        key: 'property_type',
                        render: (type: string) => <Tag color="blue">{type}</Tag>
                      },
                      {
                        title: 'Created In',
                        dataIndex: 'created_in_states',
                        key: 'created_in_states',
                        render: (states: string[]) => {
                          if (!states || states.length === 0) return '-';
                          return (
                            <Space size={4} wrap>
                              {states.map(name => (
                                <Tag key={name} color="green">{name}</Tag>
                              ))}
                            </Space>
                          );
                        }
                      },
                      {
                        title: 'Updated In',
                        dataIndex: 'updated_in_states',
                        key: 'updated_in_states',
                        render: (states: string[]) => {
                          if (!states || states.length === 0) return '-';
                          return (
                            <Space size={4} wrap>
                              {states.map(name => (
                                <Tag key={name} color="orange">{name}</Tag>
                              ))}
                            </Space>
                          );
                        }
                      }
                    ]}
                  />
                ) : (
                  <p style={{ color: '#999' }}>No property mappings defined</p>
                )}
              </div>
            </Space>
          </div>
        )}
      </Modal>

      {/* States & Transitions Editor */}
      {editingTemplateForStates && (
        <StateTemplateStatesTransitionsEditor
          template={{
            ...editingTemplateForStates,
            property_mappings: editingTemplateForStates.property_mappings || []
          }}
          visible={showStatesTransitionsModal}
          onClose={() => {
            setShowStatesTransitionsModal(false);
            setEditingTemplateForStates(null);
          }}
          onSave={async (updatedTemplateData?: any) => {
            if (updatedTemplateData && editingTemplateForStates) {
              // Use the updated template data from the save response
              const cleanedTemplate = {
                ...updatedTemplateData,
                states: deduplicateStates(updatedTemplateData.states || []),
                transitions: deduplicateTransitions(updatedTemplateData.transitions || []),
                property_mappings: updatedTemplateData.property_mappings || []
              };
              console.log('Updating editingTemplateForStates from save response with', cleanedTemplate.states?.length || 0, 'states and', cleanedTemplate.transitions?.length || 0, 'transitions');
              setEditingTemplateForStates(cleanedTemplate);
            }
            
            // Also fetch templates to refresh the list
            const response = await apiClient.get('/state-templates');
            const templates = response.data || [];
            
            // Deduplicate states and transitions for each template
            const cleanedTemplates = templates.map((template: StateTemplate) => ({
              ...template,
              states: deduplicateStates(template.states || []),
              transitions: deduplicateTransitions(template.transitions || [])
            }));
            
            setTemplates(cleanedTemplates);
            
            // Update editingTemplateForStates with the latest data from the refreshed templates if not already updated
            if (editingTemplateForStates && !updatedTemplateData) {
              const updatedTemplate = cleanedTemplates.find((t: StateTemplate) => t.id === editingTemplateForStates.id);
              if (updatedTemplate) {
                console.log('Updating editingTemplateForStates from fetch with', updatedTemplate.states?.length || 0, 'states and', updatedTemplate.transitions?.length || 0, 'transitions');
                setEditingTemplateForStates(updatedTemplate);
              } else {
                console.warn('Could not find updated template in fetched templates');
              }
            }
          }}
        />
      )}
    </div>
  );
};

export default StateTemplateLibraryManager;

